
import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  CalendarDays,
  Download,
  Share2,
} from 'lucide-react';

import { Button } from '@/shared/components/ui/button';
import {
  exportToPDF,
  exportToExcel,
  exportToDoc,
} from '@/shared/utils/exportCandidateReport';
import { mapCandidateDetails } from '@/modules/hrPortal/utils/candidateDetailsMapper';

type CandidateDetailsViewModel = ReturnType<typeof mapCandidateDetails>;
type CandidateData = CandidateDetailsViewModel['candidateData'];
type AIAnalysis = CandidateDetailsViewModel['aiAnalysis'];

type ExportData = {
  candidateData: CandidateData;
  aiAnalysis: AIAnalysis;
  currentStatus: string;
  statusHistory: CandidateData['statusHistory'];
};

interface CandidateDetailsHeaderProps {
  candidateData: CandidateData;
  aiAnalysis: AIAnalysis;
  currentStatus: string;
  canScheduleInterview: boolean;
  canAccessInterviewScheduling?: boolean;
  onBack: () => void;
  onScheduleInterview: () => void;
  onMessage: (message: string) => void;
}

export default function CandidateDetailsHeader({
  candidateData,
  aiAnalysis,
  currentStatus,
  onBack,
  onScheduleInterview,
  onMessage,
  canScheduleInterview,
  canAccessInterviewScheduling = true,
}: CandidateDetailsHeaderProps) {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const exportData: ExportData = {
    candidateData,
    aiAnalysis,
    currentStatus,
    statusHistory: candidateData.statusHistory,
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setShowExportMenu(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleShare = async () => {
    const shareData = {
      title: `Candidate - ${candidateData.name}`,
      text: `Candidate details for ${candidateData.name}`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
        onMessage('Candidate link copied to clipboard.');
      } else {
        onMessage('Sharing is not supported in this browser.');
      }
    } catch (shareError) {
      // Sharing can be cancelled by the user.
      console.log('Share cancelled:', shareError);
    }
  };

  const handleExportPDF = () => {
    exportToPDF(exportData);
    setShowExportMenu(false);
  };

  const handleExportExcel = () => {
    exportToExcel(exportData);
    setShowExportMenu(false);
  };

  const handleExportWord = async () => {
    try {
      await exportToDoc(exportData);
    } catch (error) {
      console.error('Word export failed:', error);
      onMessage('Unable to export the candidate report.');
    } finally {
      setShowExportMenu(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 lg:px-8">
        <div className="flex min-h-16 items-center justify-between gap-2 py-2">
          {/* Left: Back button and candidate details */}
          <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-4">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onBack}
              aria-label="Back"
              title="Back"
              className="h-9 w-9 shrink-0 sm:h-10 sm:w-10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div className="min-w-0 flex-1">
              <h1 className="truncate text-sm font-bold text-slate-900 sm:text-base lg:text-lg">
                {candidateData.name || 'N/A'}
              </h1>

              <p className="truncate text-[10px] text-muted-foreground sm:text-xs">
                Candidate Details
              </p>
            </div>
          </div>

          {/* Right: Share, Export and Schedule */}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {/* Share */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleShare}
              aria-label="Share candidate"
              title="Share"
              className="h-9 w-9 shrink-0 p-0 lg:h-9 lg:w-auto lg:px-3"
            >
              <Share2 className="h-4 w-4 shrink-0 lg:mr-2" />
              <span className="hidden lg:inline">Share</span>
            </Button>

            {/* Export */}
            <div ref={exportMenuRef} className="relative">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowExportMenu((value) => !value)}
                aria-label="Export candidate report"
                aria-expanded={showExportMenu}
                aria-haspopup="menu"
                title="Export"
                className="h-9 w-9 shrink-0 p-0 lg:h-9 lg:w-auto lg:px-3"
              >
                <Download className="h-4 w-4 shrink-0 lg:mr-2" />
                <span className="hidden lg:inline">Export</span>
              </Button>

              {showExportMenu && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-[100] mt-2 w-44 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg sm:w-48"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                    onClick={handleExportPDF}
                  >
                    Export PDF
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                    onClick={handleExportExcel}
                  >
                    Export Excel
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                    onClick={() => void handleExportWord()}
                  >
                    Export Word
                  </button>
                </div>
              )}
            </div>

            {/* Schedule Interview */}
            {canAccessInterviewScheduling && (
              <Button
                type="button"
                size="sm"
                onClick={onScheduleInterview}
                disabled={!canScheduleInterview}
                aria-label="Schedule interview"
                title="Schedule Interview"
                className="h-9 w-9 shrink-0 gap-0 p-0 lg:h-9 lg:w-auto lg:gap-2 lg:px-3"
              >
                <CalendarDays className="h-4 w-4 shrink-0" />
                <span className="hidden lg:inline">
                  Schedule Interview
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
